/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-09-07: SplitViewSettings for Phase 4
 * Three-section split-screen control for wall clients
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  FormControl,
  FormLabel,
  Switch,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Badge,
  IconButton,
  useColorModeValue,
  Tooltip,
  useToast,
  Collapse,
} from '@chakra-ui/react';
import { MdSettings, MdClose } from 'react-icons/md';

import { useThrottleApps, useUIStore, useUser } from '@sage3/frontend';

// Preset configurations
const SPLIT_PRESETS = {
  equal: { name: 'Equal', leftWidth: 33, middleWidth: 34 },
  focus: { name: 'Focus', leftWidth: 15, middleWidth: 70 },
  wideLeft: { name: 'Wide Left', leftWidth: 40, middleWidth: 30 },
  wideRight: { name: 'Wide Right', leftWidth: 30, middleWidth: 30 },
  balanced: { name: 'Balanced', leftWidth: 25, middleWidth: 50 },
} as const;

// Constraints
const CONSTRAINTS = {
  leftMin: 10,
  leftMax: 60,
  middleMin: 20,
  middleMax: 60,
  rightMin: 10,
};

export function SplitViewSettings() {
  const apps = useThrottleApps(250);
  const { user } = useUser();
  const toast = useToast();

  // UI Store integration (Task 3)
  const splitConfig = useUIStore((s) => s.splitConfig);
  const setSplitEnabled = useUIStore((s) => s.setSplitEnabled);
  const setSplitRatios = useUIStore((s) => s.setSplitRatios);
  const setLeftFrame = useUIStore((s) => s.setLeftFrame);
  const setRightFrame = useUIStore((s) => s.setRightFrame);
  const applySplitPreset = useUIStore((s) => s.applySplitPreset);

  // Local UI state (panel visibility)
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract config for convenience
  const { enabled: splitEnabled, leftWidth, middleWidth, leftFrameId, rightFrameId } = splitConfig;

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingBg = useColorModeValue('gray.50', 'gray.900');

  // Get all Frame apps
  const frames = useMemo(() => {
    return apps.filter((app) => app.data.type === 'Frame');
  }, [apps]);

  // Calculate right width
  const rightWidth = 100 - leftWidth - middleWidth;

  // Validate if split can be activated
  const canActivate = leftFrameId && rightFrameId;

  // Apply preset
  const handlePresetClick = (presetKey: keyof typeof SPLIT_PRESETS) => {
    const preset = SPLIT_PRESETS[presetKey];
    applySplitPreset(presetKey);

    toast({
      title: `${preset.name} Preset Applied`,
      description: `${preset.leftWidth}-${preset.middleWidth}-${100 - preset.leftWidth - preset.middleWidth}`,
      status: 'success',
      duration: 1500,
      isClosable: true,
      position: 'top-right',
    });
  };

  // Handle left width change
  const handleLeftWidthChange = (value: number) => {
    const oldMiddle = middleWidth;
    setSplitRatios(value, middleWidth);

    // Check if auto-adjustment happened
    const newRight = 100 - value - middleWidth;
    if (newRight < CONSTRAINTS.rightMin) {
      const newMiddle = 90 - value;
      if (newMiddle >= CONSTRAINTS.middleMin && newMiddle !== oldMiddle) {
        toast({
          title: 'Ratio Adjusted',
          description: `Middle reduced to ${newMiddle}% to keep right ≥ 10%`,
          status: 'info',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      } else if (newMiddle < CONSTRAINTS.middleMin) {
        toast({
          title: 'Cannot Adjust',
          description: 'This ratio would violate minimum constraints',
          status: 'warning',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  };

  // Handle middle width change
  const handleMiddleWidthChange = (value: number) => {
    const oldLeft = leftWidth;
    setSplitRatios(leftWidth, value);

    // Check if auto-adjustment happened
    const newRight = 100 - leftWidth - value;
    if (newRight < CONSTRAINTS.rightMin) {
      const newLeft = 90 - value;
      if (newLeft >= CONSTRAINTS.leftMin && newLeft !== oldLeft) {
        toast({
          title: 'Ratio Adjusted',
          description: `Left reduced to ${newLeft}% to keep right ≥ 10%`,
          status: 'info',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      } else if (newLeft < CONSTRAINTS.leftMin) {
        toast({
          title: 'Cannot Adjust',
          description: 'This ratio would violate minimum constraints',
          status: 'warning',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  };

  // Handle split toggle
  const handleSplitToggle = () => {
    if (!splitEnabled && !canActivate) {
      toast({
        title: 'Cannot Activate Split',
        description: 'Please select both left and right frames first',
        status: 'warning',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    const newState = !splitEnabled;
    setSplitEnabled(newState);

    toast({
      title: newState ? 'Split Mode Activated' : 'Split Mode Disabled',
      description: newState ? `${leftWidth}-${middleWidth}-${rightWidth} layout` : 'Returned to single viewport',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  // Only render for wall clients
  if (user?.data.userType !== 'wall') {
    return null;
  }

  return (
    <Box
      position="fixed"
      top="4"
      right="4"
      zIndex={10000}
    >
      {/* Toggle button when collapsed */}
      {!isExpanded && (
        <Tooltip label="Split View Settings" placement="left" hasArrow>
          <IconButton
            aria-label="Open split view settings"
            icon={<MdSettings size={24} />}
            size="lg"
            colorScheme="teal"
            onClick={() => setIsExpanded(true)}
            bg={bgColor}
            borderWidth="2px"
            borderColor={borderColor}
            shadow="xl"
          />
        </Tooltip>
      )}

      {/* Settings panel when expanded */}
      <Collapse in={isExpanded} animateOpacity>
        <Box
          bg={bgColor}
          borderRadius="lg"
          borderWidth="2px"
          borderColor={borderColor}
          shadow="2xl"
          width="380px"
          maxHeight="80vh"
          overflowY="auto"
        >
          {/* Header */}
          <HStack
            justify="space-between"
            align="center"
            px={4}
            py={3}
            bg={headingBg}
            borderTopRadius="md"
          >
            <HStack spacing={2}>
              <MdSettings size={20} />
              <Text fontWeight="bold" fontSize="lg">
                Wall Split View
              </Text>
            </HStack>
            <IconButton
              aria-label="Close settings"
              icon={<MdClose />}
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(false)}
            />
          </HStack>

          {/* Content */}
          <VStack spacing={4} px={4} py={4} align="stretch">
            {/* Split Mode Toggle */}
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel htmlFor="split-mode" mb="0" fontWeight="medium">
                Split Mode Active
              </FormLabel>
              <Switch
                id="split-mode"
                size="lg"
                colorScheme="teal"
                isChecked={splitEnabled}
                onChange={handleSplitToggle}
              />
            </FormControl>

            {/* Status indicator */}
            {splitEnabled && (
              <Badge colorScheme="green" fontSize="sm" px={3} py={1} textAlign="center">
                ✓ Split Active: {leftWidth}-{middleWidth}-{rightWidth}
              </Badge>
            )}

            {/* Divider */}
            <Box borderTop="1px" borderColor={borderColor} />

            {/* Preset Buttons */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Quick Presets:
              </Text>
              <VStack spacing={2}>
                <HStack spacing={2} width="100%">
                  <Button
                    size="sm"
                    flex={1}
                    onClick={() => handlePresetClick('equal')}
                    colorScheme="gray"
                    variant="outline"
                  >
                    Equal
                    <Text fontSize="xs" ml={1} color="gray.500">
                      33-34-33
                    </Text>
                  </Button>
                  <Button
                    size="sm"
                    flex={1}
                    onClick={() => handlePresetClick('focus')}
                    colorScheme="gray"
                    variant="outline"
                  >
                    Focus
                    <Text fontSize="xs" ml={1} color="gray.500">
                      15-70-15
                    </Text>
                  </Button>
                </HStack>
                <HStack spacing={2} width="100%">
                  <Button
                    size="sm"
                    flex={1}
                    onClick={() => handlePresetClick('wideLeft')}
                    colorScheme="gray"
                    variant="outline"
                  >
                    Wide Left
                    <Text fontSize="xs" ml={1} color="gray.500">
                      40-30-30
                    </Text>
                  </Button>
                  <Button
                    size="sm"
                    flex={1}
                    onClick={() => handlePresetClick('wideRight')}
                    colorScheme="gray"
                    variant="outline"
                  >
                    Wide Right
                    <Text fontSize="xs" ml={1} color="gray.500">
                      30-30-40
                    </Text>
                  </Button>
                </HStack>
                <Button
                  size="sm"
                  width="100%"
                  onClick={() => handlePresetClick('balanced')}
                  colorScheme="gray"
                  variant="outline"
                >
                  Balanced
                  <Text fontSize="xs" ml={1} color="gray.500">
                    25-50-25
                  </Text>
                </Button>
              </VStack>
            </Box>

            {/* Custom Ratios */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>
                Custom Ratios:
              </Text>

              {/* Left slider */}
              <FormControl>
                <FormLabel fontSize="sm">
                  Left: {leftWidth}%
                </FormLabel>
                <Slider
                  value={leftWidth}
                  min={CONSTRAINTS.leftMin}
                  max={CONSTRAINTS.leftMax}
                  step={5}
                  onChange={handleLeftWidthChange}
                  colorScheme="teal"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={6}>
                    <Box color="teal.500" fontSize="xs">
                      L
                    </Box>
                  </SliderThumb>
                </Slider>
              </FormControl>

              {/* Middle slider */}
              <FormControl mt={3}>
                <FormLabel fontSize="sm">
                  Middle: {middleWidth}%
                </FormLabel>
                <Slider
                  value={middleWidth}
                  min={CONSTRAINTS.middleMin}
                  max={CONSTRAINTS.middleMax}
                  step={5}
                  onChange={handleMiddleWidthChange}
                  colorScheme="teal"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={6}>
                    <Box color="teal.500" fontSize="xs">
                      M
                    </Box>
                  </SliderThumb>
                </Slider>
              </FormControl>

              {/* Right auto-calculated */}
              <FormControl mt={3}>
                <FormLabel fontSize="sm">
                  Right: {rightWidth}% (auto)
                </FormLabel>
                <Box
                  height="10px"
                  bg={useColorModeValue('gray.200', 'gray.700')}
                  borderRadius="full"
                  position="relative"
                  overflow="hidden"
                >
                  <Box
                    height="100%"
                    width={`${rightWidth}%`}
                    bg="teal.500"
                    borderRadius="full"
                    transition="width 0.2s"
                  />
                </Box>
              </FormControl>

              {/* Validation warning */}
              {rightWidth < CONSTRAINTS.rightMin && (
                <Badge colorScheme="red" fontSize="xs" mt={2}>
                  ⚠ Right section too narrow ({rightWidth}% &lt; 10%)
                </Badge>
              )}
            </Box>

            {/* Divider */}
            <Box borderTop="1px" borderColor={borderColor} />

            {/* Frame Selection */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Select Reference Regions:
              </Text>

              {/* No frames warning */}
              {frames.length === 0 && (
                <Badge colorScheme="orange" fontSize="sm" py={2} px={3} textAlign="center" width="100%">
                  No Frame apps available. Create Frames first.
                </Badge>
              )}

              {/* Left frame dropdown */}
              <FormControl mt={2}>
                <FormLabel fontSize="sm">Left Region:</FormLabel>
                <Select
                  placeholder="-- Select Frame --"
                  value={leftFrameId || ''}
                  onChange={(e) => setLeftFrame(e.target.value || null)}
                  size="sm"
                  isDisabled={frames.length === 0}
                >
                  {frames.map((frame) => (
                    <option key={frame._id} value={frame._id}>
                      {frame.data.title || 'Untitled Frame'}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Right frame dropdown */}
              <FormControl mt={3}>
                <FormLabel fontSize="sm">Right Region:</FormLabel>
                <Select
                  placeholder="-- Select Frame --"
                  value={rightFrameId || ''}
                  onChange={(e) => setRightFrame(e.target.value || null)}
                  size="sm"
                  isDisabled={frames.length === 0}
                >
                  {frames.map((frame) => (
                    <option key={frame._id} value={frame._id}>
                      {frame.data.title || 'Untitled Frame'}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Help text */}
            <Box>
              <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')}>
                Split divides the wall into three sections: left and right show static reference content (Frames),
                while middle follows the presenter's viewport.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
}
